'use client';
import { useStore } from '@/providers/app-store-provider';
import type { IRouterInfo } from '@/store/slices/routeSlice';
import logger from '@/utils/client-logger';
import { removeBlockFlags } from '@/utils/feature-value';
import { getRouteCnName, getRouteName } from '@/utils/route-metadata';
import { recordCartOfferRoute } from '@/domain/cart/auto-offer/entry';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

export default function PathnameTracker() {
  const pathname = usePathname();
  const [path, setPath] = useState<string>('');
  const setCurrentRouteInfo = useStore((state) => state.setCurrentRouteInfo);
  const setFromRouterInfo = useStore((state) => state.setFromRouterInfo);

  // 用于存储当前路由信息的引用
  const currentRouteRef = useRef<IRouterInfo | null>(null);
  // 用于标记是否是首次加载
  const isInitialLoad = useRef(true);

  useEffect(() => {
    // 只记录实际离开购物车，不改共享优惠；回到购物车后由其首次恢复统一处理。
    // 依赖 pathname 而非组件卸载，避免刷新、登录门控和 StrictMode 误清指定券。
    // 各站自动组共用入场规则；这里只记访问轮次，是否恢复自动仍由购物车分组决定。
    recordCartOfferRoute(pathname);
  }, [pathname]);

  // 构建路由信息对象
  const buildRouteInfo = (_path: string): IRouterInfo => {
    const pageName = getRouteCnName(_path) || getRouteName(_path) || '其他';
    const refer_url_path = typeof window !== 'undefined' ? window.location.href : _path;

    const routeInfo: IRouterInfo = {
      refer_page_name: pageName,
      refer_url_path,
      refer_path_name: _path,
    };

    return routeInfo;
  };

  // 解析并安全处理路由信息
  const parseSafeRouteInfo = useCallback((routeInfoString: string): IRouterInfo | null => {
    try {
      const raw = JSON.parse(routeInfoString) as Partial<IRouterInfo> | null;
      return {
        refer_page_name: typeof raw?.refer_page_name === 'string' ? raw.refer_page_name : '',
        refer_url_path: typeof raw?.refer_url_path === 'string' ? raw.refer_url_path : '',
        refer_path_name: typeof raw?.refer_path_name === 'string' ? raw.refer_path_name : '',
      };
    } catch (e) {
      logger.error('PathnameTracker parseSafeRouteInfo', e);
      return null;
    }
  }, []);

  // 处理路由变化
  const handleRouteChange = useCallback(
    (newPath: string) => {
      if (typeof window === 'undefined') return;

      // 构建新的路由信息
      const newRouteInfo = buildRouteInfo(newPath);

      // 1. 先从 sessionStorage 读取当前路由信息作为前一个路由
      const prevRouteInfoString = sessionStorage.getItem('zl-current-route-info');
      if (prevRouteInfoString) {
        try {
          const prevRouteInfo = parseSafeRouteInfo(prevRouteInfoString);
          if (prevRouteInfo && prevRouteInfo.refer_path_name !== newPath) {
            // 2. 设置前一个路由信息
            setFromRouterInfo(prevRouteInfo);
            sessionStorage.setItem('zl-from-route-info', prevRouteInfoString);
          }
        } catch (e) {
          logger.error('PathnameTracker handleRouteChange', e);
        }
      }

      // 3. 最后更新当前路由信息
      currentRouteRef.current = newRouteInfo;
      setCurrentRouteInfo(newRouteInfo);
      sessionStorage.setItem('zl-current-route-info', JSON.stringify(newRouteInfo));
    },
    [buildRouteInfo, setCurrentRouteInfo, setFromRouterInfo],
  );

  // 处理浏览器的前进/后退
  const handlePopState = useCallback(() => {
    const prevInfo = sessionStorage.getItem('zl-current-route-info');
    if (prevInfo) {
      const safeRouteInfo = parseSafeRouteInfo(prevInfo);
      if (safeRouteInfo) {
        setFromRouterInfo(safeRouteInfo);
      }
    }
  }, [parseSafeRouteInfo, setFromRouterInfo]);

  const updatePathFromLocation = useCallback(() => {
    if (typeof window === 'undefined') return;
    setPath(window.location.pathname + window.location.search);
  }, []);

  useEffect(() => {
    updatePathFromLocation();
  }, [pathname, updatePathFromLocation]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleHistoryChange = () => {
      updatePathFromLocation();
    };
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function pushState(...args) {
      const result = originalPushState.apply(this, args);
      window.dispatchEvent(new Event('zl-history-change'));
      return result;
    };
    window.history.replaceState = function replaceState(...args) {
      const result = originalReplaceState.apply(this, args);
      window.dispatchEvent(new Event('zl-history-change'));
      return result;
    };

    window.addEventListener('popstate', handleHistoryChange);
    window.addEventListener('zl-history-change', handleHistoryChange);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handleHistoryChange);
      window.removeEventListener('zl-history-change', handleHistoryChange);
    };
  }, [updatePathFromLocation]);

  useEffect(() => {
    if (typeof window === 'undefined' || !path) return;

    if (isInitialLoad.current) {
      // 首次加载时，只设置当前路由信息，不设置来源路由信息
      isInitialLoad.current = false;
      const newRouteInfo = buildRouteInfo(path);
      currentRouteRef.current = newRouteInfo;
      setCurrentRouteInfo(newRouteInfo, true); // 传入 true 跳过设置来源路由
      sessionStorage.removeItem('zl-from-route-info');
      sessionStorage.setItem('zl-current-route-info', JSON.stringify(newRouteInfo));
    } else {
      // 后续路由变化时才执行 handleRouteChange
      handleRouteChange(path);
    }

    // 添加 popstate 事件监听
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [buildRouteInfo, handlePopState, handleRouteChange, path, setCurrentRouteInfo]);
  // 处理商品详情页的标记清除
  useEffect(() => {
    try {
      // 定义需要保留 block_flags 的页面路径
      const shouldKeepBlockFlags =
        pathname.startsWith('/goods-detail/') ||
        pathname.startsWith('/lens-process') ||
        pathname.startsWith('/frame-process');

      if (!shouldKeepBlockFlags) {
        removeBlockFlags();
      }
    } catch (e) {
      logger.error('PathnameTracker removeBlockFlags', e);
    }
  }, [pathname]);

  // 这个组件不渲染任何内容，只负责跟踪路径
  return null;
}
